import type { FC } from "react";
import React from "react";
import { StorageController } from "src/state/StorageBloc/StorageBloc";
import { authenticationState } from "src/state/state";
import {
  DebugBadge,
  DebugButton,
  DebugButtonRow,
  DebugInfoBox,
  DebugKeyValue
} from "./debug.styles";
import type { UserCandidate } from "./DebugUserStorage";

interface StorageActionsProps {
  userCount: number;
  userSelectionResult: {
    selectedUserId: string | null;
    selectedStorage: string | null;
    allCandidates: UserCandidate[];
  } | null;
  hasDataConflict: boolean;
  onLoadUsers: () => void;
  onLogDetails: () => void;
  onForceLogActive: () => void;
}

const truncateId = (id: string | null) => {
  if (!id) return "none";
  if (id.length <= 16) return id;
  return `${id.slice(0, 8)}...${id.slice(-6)}`;
};

const StorageActions: FC<StorageActionsProps> = ({
  userCount,
  userSelectionResult,
  hasDataConflict,
  onLoadUsers,
  onLogDetails,
  onForceLogActive
}) => {
  return (
    <div>
      <DebugButtonRow>
        <DebugButton onClick={onLoadUsers}>Refresh</DebugButton>
        <DebugButton onClick={onLogDetails}>Log to Console</DebugButton>
        <DebugButton onClick={onForceLogActive}>Force Log Active</DebugButton>
      </DebugButtonRow>

      <div style={{ marginTop: "0.75rem" }}>
        <DebugKeyValue>
          <span className="key">Users Found</span>
          <span className="value">
            <DebugBadge variant={userCount > 0 ? "info" : "warning"}>
              {userCount}
            </DebugBadge>
          </span>
        </DebugKeyValue>
        <DebugKeyValue>
          <span className="key">Active User</span>
          <span className="value mono" style={{ fontSize: "11px" }}>
            {truncateId(StorageController.activeUserId)}
          </span>
        </DebugKeyValue>
        <DebugKeyValue>
          <span className="key">Storage</span>
          <span className="value">
            <DebugBadge
              variant={
                StorageController.useStorage === localStorage
                  ? "info"
                  : "warning"
              }
            >
              {StorageController.useStorage === localStorage
                ? "localStorage"
                : "sessionStorage"}
            </DebugBadge>
          </span>
        </DebugKeyValue>
        <DebugKeyValue>
          <span className="key">Auth Stage</span>
          <span className="value">
            <DebugBadge
              variant={
                authenticationState.state.authStage === "fullAuth"
                  ? "success"
                  : "info"
              }
            >
              {authenticationState.state.authStage || "none"}
            </DebugBadge>
          </span>
        </DebugKeyValue>
      </div>

      {userSelectionResult && userSelectionResult.allCandidates.length > 1 && (
        <DebugInfoBox style={{ marginTop: "0.75rem" }}>
          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>
            Selection Logic
          </p>
          <p style={{ fontSize: "12px" }}>
            Would select:{" "}
            <code>{truncateId(userSelectionResult.selectedUserId)}</code>
          </p>
          <p style={{ fontSize: "12px" }}>
            From: <code>{userSelectionResult.selectedStorage || "none"}</code>
          </p>
          <p style={{ fontSize: "12px" }}>
            Candidates: {userSelectionResult.allCandidates.length}
          </p>
        </DebugInfoBox>
      )}

      {hasDataConflict && (
        <DebugInfoBox variant="warning" style={{ marginTop: "0.75rem" }}>
          <p style={{ fontWeight: 600 }}>Data Conflict Detected</p>
          <p style={{ fontSize: "12px" }}>
            Some users have different authStage values in localStorage vs
            sessionStorage.
          </p>
        </DebugInfoBox>
      )}
    </div>
  );
};

export default StorageActions;
